import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "./firebase";

const functions = getFunctions(app);

// Types
export interface PromoCodeResult {
  success: boolean;
  type?: "premium" | "trial" | "bonus";
  message?: string;
  description?: string;
}

export interface CheckoutResult {
  sessionId: string;
  url: string;
}

export interface PromoCode {
  code: string;
  type: "premium" | "trial" | "bonus";
  durationDays?: number;
  bonusUsage?: Record<string, number>;
  maxRedemptions?: number;
  currentRedemptions: number;
  expiresAt?: string;
  isActive: boolean;
  createdAt: string;
  description?: string;
}

// Promo code functions
export async function redeemPromoCode(code: string): Promise<PromoCodeResult> {
  const redeemFn = httpsCallable<{ code: string }, PromoCodeResult>(functions, "redeemPromoCode");
  const result = await redeemFn({ code });
  return result.data;
}

// Admin functions
export async function createPromoCode(promoCode: Partial<PromoCode>): Promise<{ success: boolean; code: string }> {
  const createFn = httpsCallable<Partial<PromoCode>, { success: boolean; code: string }>(functions, "createPromoCode");
  const result = await createFn(promoCode);
  return result.data;
}

export async function listPromoCodes(): Promise<PromoCode[]> {
  const listFn = httpsCallable<void, { codes: PromoCode[] }>(functions, "listPromoCodes");
  const result = await listFn();
  return result.data.codes;
}

export async function deactivatePromoCode(code: string): Promise<void> {
  const deactivateFn = httpsCallable<{ code: string }, { success: boolean }>(functions, "deactivatePromoCode");
  await deactivateFn({ code });
}

export async function deletePromoCode(code: string): Promise<void> {
  const deleteFn = httpsCallable<{ code: string }, { success: boolean }>(functions, "deletePromoCode");
  await deleteFn({ code });
}

// Account deletion (server-side: deletes Firestore + Auth + cancels Stripe)
export async function deleteAccountFn(): Promise<{ success: boolean }> {
  const deleteFn = httpsCallable<void, { success: boolean }>(functions, "deleteAccount");
  const result = await deleteFn();
  return result.data;
}

// Admin status check (reads from protected admins collection via Cloud Function)
export async function checkAdminStatusFn(): Promise<{ isAdmin: boolean }> {
  const checkFn = httpsCallable<void, { isAdmin: boolean }>(functions, "checkAdminStatus");
  const result = await checkFn();
  return result.data;
}

// Stripe functions
export async function createCheckoutSession(
  successUrl: string,
  cancelUrl: string,
  priceId?: string
): Promise<CheckoutResult> {
  const checkoutFn = httpsCallable<
    { priceId?: string; successUrl: string; cancelUrl: string },
    CheckoutResult
  >(functions, "createCheckoutSession");

  const result = await checkoutFn({ priceId, successUrl, cancelUrl });
  return result.data;
}

export async function createBillingPortalSession(returnUrl: string): Promise<{ url: string }> {
  const portalFn = httpsCallable<{ returnUrl: string }, { url: string }>(functions, "createBillingPortalSession");
  const result = await portalFn({ returnUrl });
  return result.data;
}
